import type { AssetGsiItem } from '@/app/api/incidents/asset-gsi.types';

export type GsiWeekPoint = { date: string; gsi: number };

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local midnight timestamp for a given time. */
const startOfDay = (time: number): number => {
  const d = new Date(time);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** `YYYY-MM-DD` from a Date's LOCAL components (avoids the UTC off-by-one of toISOString). */
const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const GSI_SUPPORTED_ASSETS = [
  'United States',
  'Iran',
  'India',
  'China',
  'Venezuela',
  'Taiwan',
  'Brazil',
  'Canada',
  'DR Congo',
  'Ethiopia',
  'France',
  'Germany',
  'Cuba',
  'Indonesia',
  'Mexico',
  'Myanmar',
  'Sudan',
];

export const isGsiSupported = (location: string | null | undefined): boolean => {
  if (!location) {
    return false;
  }

  return GSI_SUPPORTED_ASSETS.includes(location);
};

/**
 * Aggregates GSI incidents into weekly averages using 7-day windows anchored on TODAY
 * (window 0 = the last 7 days, i.e. dates >= today - 7, matching the BigQuery
 * `DATE(IncidentDateTime) >= CURRENT_DATE - 7` semantics), sorted oldest → newest.
 * Each point is labeled by the latest incident date inside its window. Single source of truth
 * shared by the trend chart and the score widget, so "latest GSI" equals the last chart point.
 *
 * @param months - Optional look-back window; when omitted, all data is included.
 */
export const aggregateWeeklyGsi = (items: AssetGsiItem[], months?: number): GsiWeekPoint[] => {
  let cutoff = -Infinity;
  if (months != null) {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - months);
    cutoff = cutoffDate.getTime();
  }

  const filtered = items
    .filter(
      (item): item is AssetGsiItem & { GSI: number } =>
        typeof item.GSI === 'number' && new Date(item.DateTime).getTime() >= cutoff,
    )
    .sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime());

  if (filtered.length === 0) {
    return [];
  }

  const todayStart = startOfDay(Date.now());

  const weeks = new Map<number, { sum: number; count: number; latest: number }>();
  for (const item of filtered) {
    const time = new Date(item.DateTime).getTime();
    const daysAgo = Math.round((todayStart - startOfDay(time)) / DAY_MS);
    // window 0 = last 7 days (dates >= today - 7); older data falls into 7-day windows back.
    const weekIndex = Math.max(0, Math.ceil(daysAgo / 7) - 1);
    const entry = weeks.get(weekIndex) ?? { sum: 0, count: 0, latest: time };
    entry.sum += item.GSI;
    entry.count += 1;
    entry.latest = Math.max(entry.latest, time);
    weeks.set(weekIndex, entry);
  }

  return Array.from(weeks.entries())
    .sort(([a], [b]) => b - a) // oldest window (largest index) first
    .map(([, { sum, count, latest }]) => ({
      date: toLocalDateKey(new Date(latest)),
      gsi: Math.round(sum / count),
    }));
};

/** GSI of the most recent week that has data (matches the last point of the trend chart). */
export const getAverageGsi = (items: AssetGsiItem[]): number | null => {
  const points = aggregateWeeklyGsi(items);
  return points.length ? points[points.length - 1].gsi : null;
};

/** GSI of the previous week that has data — used for the "vs last week" delta. */
export const getPreviousWeekAverageGsi = (items: AssetGsiItem[]): number | null => {
  const points = aggregateWeeklyGsi(items);
  return points.length >= 2 ? points[points.length - 2].gsi : null;
};
